import React from 'react'
import { useTranslation } from 'react-i18next'
import { X, AlertTriangle } from 'lucide-react'

/**
 * Универсальное модальное окно подтверждения действия
 * @param {boolean} isOpen - Открыто ли модальное окно
 * @param {function} onClose - Функция закрытия модального окна
 * @param {function} onConfirm - Функция подтверждения действия
 * @param {string} title - Заголовок модального окна
 * @param {string} message - Сообщение в модальном окне
 * @param {string} confirmText - Текст кнопки подтверждения
 * @param {string} cancelText - Текст кнопки отмены
 * @param {boolean} isLoading - Состояние загрузки
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isLoading = false
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-dark-secondary rounded-xl border border-white/10 shadow-2xl bg-black">
        {/* Кнопка закрытия */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-2 text-text-secondary hover:text-white transition-colors disabled:opacity-50"
        >
          <X size={20} />
        </button>

        {/* Контент */}
        <div className="p-6">
          {/* Иконка предупреждения */}
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-yellow-500/20 rounded-full">
              <AlertTriangle className="text-yellow-500" size={32} />
            </div>
          </div>

          {/* Заголовок */}
          <h3 className="text-xl font-bold text-center mb-3">
            {title}
          </h3>

          {/* Сообщение */}
          <p className="text-text-secondary text-center mb-6">
            {message}
          </p>

          {/* Кнопки действий */}
          <div className="flex gap-3">
            {/* Кнопка отмены */}
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-surface hover:bg-surface/80 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {cancelText || t('common.cancel')}
            </button>

            {/* Кнопка подтверждения */}
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 text-dark-primary rounded-lg font-bold transition-opacity disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-dark-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                confirmText || t('common.confirm')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
